import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
  type ForwardedRef,
} from 'react';
import { NamedIcon } from '../../icons';
import { cn } from '../../utils/cn';
import {
  baseStyles,
  buttonHeightStyles,
  buttonTextStyles,
  defaultPaddingStyles,
  getDefaultTypeIconName,
  getIconSize,
  iconOnlyPaddingStyles,
  linkIconGapStyles,
  linkTextStyles,
  variantStyles,
} from './CompButton.styles';
import { CompButtonDefaultDrawer } from './CompButton.drawer';
import { CompButtonFormActionContent } from './CompButton.form-action';

export const compButtonVariants = ['primary', 'secondary', 'link'] as const;
export type CompButtonVariant = (typeof compButtonVariants)[number];

export const compButtonSizes = ['xs', 's', 'm', 'l'] as const;
export type CompButtonSize = (typeof compButtonSizes)[number];

export const compButtonAreas = [
  'residential',
  'professionals',
  'commercial',
  'alternative',
] as const;
export type CompButtonArea = (typeof compButtonAreas)[number];

export const compButtonIconPositions = ['leading', 'trailing'] as const;
export type CompButtonIconPosition = (typeof compButtonIconPositions)[number];

export const compButtonTypes = ['button', 'page', 'anchor', 'drawer', 'form-action'] as const;
export type CompButtonType = (typeof compButtonTypes)[number];

export const compButtonDrawerLayouts = ['horizontal', 'vertical'] as const;
export type CompButtonDrawerLayout = (typeof compButtonDrawerLayouts)[number];

export const compButtonDrawerScales = ['full', 'half', 'offset'] as const;
export type CompButtonDrawerScale = (typeof compButtonDrawerScales)[number];

export interface CompButtonCustomLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
  children: React.ReactNode;
  className: string;
  href?: string;
  to?: string;
}

export interface CompButtonCustomDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  drawerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ariaLabelDrawer?: string;
  ariaLabelDrawerButton?: string;
  layout?: CompButtonDrawerLayout;
  scale?: CompButtonDrawerScale;
}

export interface CompButtonActionStates {
  initial?: boolean;
  loading?: boolean;
  feedback?: boolean;
  success?: boolean;
  error?: boolean;
}

export interface CompButtonButtonActionsContext {
  errorText?: React.ReactNode;
  successText?: React.ReactNode;
  feedbackText?: React.ReactNode;
  initialText?: React.ReactNode;
  loadingText?: React.ReactNode;
}

export interface CompButtonFormActionSlots {
  initial?: React.ReactNode;
  loading?: React.ReactNode;
  success?: React.ReactNode;
  feedback?: React.ReactNode;
  error?: React.ReactNode;
}

export interface CompButtonTrackPayload {
  customTrackingData?: Record<string, unknown>;
  trackingIconName?: string;
}

export type CompButtonTrackHandler = (
  event: React.MouseEvent<HTMLElement>,
  payload: CompButtonTrackPayload
) => void;

interface CompButtonContextValue {
  customButtonColorFlag?: boolean;
  onTrack?: CompButtonTrackHandler;
}

export interface CompButtonProviderProps extends CompButtonContextValue {
  children: React.ReactNode;
}

const CompButtonContext = createContext<CompButtonContextValue | undefined>(undefined);

const CompButtonProvider = ({
  children,
  customButtonColorFlag,
  onTrack,
}: CompButtonProviderProps) => {
  const value = useMemo(
    () => ({ customButtonColorFlag, onTrack }),
    [customButtonColorFlag, onTrack]
  );

  return <CompButtonContext.Provider value={value}>{children}</CompButtonContext.Provider>;
};

CompButtonProvider.displayName = 'CompButtonProvider';

interface SharedCompButtonProps {
  text?: React.ReactNode;
  variant?: CompButtonVariant;
  size?: CompButtonSize;
  area?: CompButtonArea;
  alternateHover?: boolean;
  isDisabled?: boolean;
  onDark?: boolean;
  children?: React.ReactNode;
  type?: CompButtonType;
  buttonType?: NonNullable<ButtonHTMLAttributes<HTMLButtonElement>['type']>;
  buttonActionsContext?: CompButtonButtonActionsContext;
  customButtonColorFlag?: boolean;
  customTrackingData?: Record<string, unknown>;
  autoHeight?: boolean;
  formActionSlots?: CompButtonFormActionSlots;
  iconName?: string;
  iconPosition?: CompButtonIconPosition;
  iconOnly?: boolean;
  iconSize?: number;
  actionsStates?: CompButtonActionStates;
  ariaLabelButton?: string;
  ariaLabelDrawer?: string;
  ariaLabelDrawerButton?: string;
  ariaLabelLoadingState?: string;
  ariaLabelSuccessState?: string;
  ariaLabelErrorState?: string;
  ariaLabelFeedbackState?: string;
  drawerLayout?: CompButtonDrawerLayout;
  drawerScale?: CompButtonDrawerScale;
  drawerContent?: React.ReactNode;
  drawerComponent?: ElementType<CompButtonCustomDrawerProps>;
  drawerComponentProps?: Record<string, unknown>;
  onTrack?: CompButtonTrackHandler;
  openDrawer?: boolean;
  onDrawerOpenChange?: (open: boolean) => void;
  to?: string;
  linkComponent?: ElementType<CompButtonCustomLinkProps>;
  linkComponentProps?: Record<string, unknown>;
}

type CompButtonAsButtonProps = SharedCompButtonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled' | 'type' | 'children'> & {
    href?: undefined;
    to?: undefined;
    linkComponent?: undefined;
  };

type CompButtonAsAnchorProps = SharedCompButtonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> &
  (
    | {
        href: string;
        to?: undefined;
        linkComponent?: undefined;
      }
    | {
        to: string;
        href?: undefined;
        linkComponent?: undefined;
      }
  );

type CompButtonAsCustomLinkProps = SharedCompButtonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'href'> & {
    linkComponent: ElementType<CompButtonCustomLinkProps>;
    href?: string;
  };

export type CompButtonProps =
  | CompButtonAsButtonProps
  | CompButtonAsAnchorProps
  | CompButtonAsCustomLinkProps;

type CompButtonElement = HTMLButtonElement | HTMLAnchorElement;
type CompButtonActionStateName = keyof Omit<CompButtonActionStates, 'initial'>;
export type CompButtonFormActionState = 'initial' | 'loading' | 'success' | 'feedback' | 'error';

interface CompButtonIconProps {
  name: string;
  size: number;
  className?: string;
  testId: string;
}

const CompButtonIcon = ({ name, size, className, testId }: CompButtonIconProps) => {
  return (
    <NamedIcon
      name={name}
      size={size}
      aria-hidden="true"
      className={cn('shrink-0', className)}
      data-testid={testId}
    />
  );
};

const CompButton = forwardRef<CompButtonElement, CompButtonProps>(
  (props: CompButtonProps, ref: ForwardedRef<CompButtonElement>) => {
    const context = useContext(CompButtonContext);
    const {
      actionsStates,
      alternateHover = false,
      area = 'residential',
      ariaLabelButton,
      ariaLabelDrawer,
      ariaLabelDrawerButton,
      ariaLabelErrorState,
      ariaLabelFeedbackState,
      ariaLabelLoadingState,
      ariaLabelSuccessState,
      autoHeight = false,
      buttonType,
      buttonActionsContext,
      children,
      className,
      customButtonColorFlag,
      customTrackingData,
      drawerLayout = 'horizontal',
      drawerScale = 'full',
      drawerComponent: DrawerComponent,
      drawerComponentProps,
      drawerContent,
      formActionSlots,
      href,
      iconName,
      iconOnly = false,
      iconPosition,
      iconSize,
      isDisabled = false,
      linkComponent: LinkComponent,
      linkComponentProps,
      onTrack,
      onDrawerOpenChange,
      onDark = false,
      openDrawer,
      size = 's',
      text,
      to,
      type = 'button',
      variant = 'primary',
      ...elementProps
    } = props;

    const content = children ?? text;
    const resolvedButtonText =
      typeof content === 'string' ? content : typeof text === 'string' ? text : undefined;
    const generatedDrawerId = useId().replace(/:/g, '');
    const [drawerIsVisible, setDrawerIsVisible] = useState(Boolean(openDrawer));
    const resolvedTrackHandlers = [context?.onTrack, onTrack].filter(
      (handler): handler is CompButtonTrackHandler => Boolean(handler)
    );
    const resolvedCustomButtonColorFlag =
      customButtonColorFlag ?? context?.customButtonColorFlag ?? false;

    useEffect(() => {
      if (typeof openDrawer === 'boolean') {
        setDrawerIsVisible(openDrawer);
      }
    }, [openDrawer]);

    const explicitAriaLabel = elementProps['aria-label'];
    const resolvedAriaLabel = !isDisabled
      ? (ariaLabelButton ?? explicitAriaLabel ?? resolvedButtonText)
      : undefined;
    const hasDrawerContent = drawerContent !== undefined && drawerContent !== null;
    const showDrawer = type === 'drawer' && (hasDrawerContent || Boolean(DrawerComponent));
    const resolvedDrawerId = `comp-button-drawer-${generatedDrawerId}`;
    const isFormAction = type === 'form-action';
    const isCustomVariant = variant === 'primary' && resolvedCustomButtonColorFlag && !onDark;
    const hasDefaultTypeIcon = type === 'page' || type === 'anchor' || type === 'drawer';
    const resolvedIconName = hasDefaultTypeIcon ? getDefaultTypeIconName(type) : iconName;
    const resolvedIconSize = getIconSize(iconSize, size, hasDefaultTypeIcon, variant);
    const resolvedButtonType = buttonType ?? (showDrawer ? 'button' : 'submit');
    const resolvedNavigationHref = href ?? to;
    const palette = isCustomVariant
      ? cn(
          isDisabled
            ? 'bg-velux-button-primary-disabled text-velux-grey-50 disabled:bg-velux-button-primary-disabled disabled:text-velux-grey-50 aria-disabled:bg-velux-button-primary-disabled aria-disabled:text-velux-grey-50'
            : 'bg-velux-yellow-custom text-velux-button-secondary-initial',
          !isDisabled && !alternateHover && 'hover:bg-velux-yellow-custom-dark',
          !isDisabled &&
            alternateHover &&
            'hover:bg-velux-button-secondary-initial hover:text-velux-white'
        )
      : onDark
        ? variantStyles[variant].onDark
        : variantStyles[variant][area];
    const sizing =
      variant === 'link'
        ? linkTextStyles[size]
        : cn(buttonTextStyles[size], !autoHeight && buttonHeightStyles[size]);
    const padding =
      variant === 'link' ? '' : iconOnly ? iconOnlyPaddingStyles[size] : defaultPaddingStyles[size];
    const gapClass =
      variant === 'link'
        ? hasDefaultTypeIcon
          ? size === 'xs' || size === 's'
            ? 'gap-1'
            : 'gap-2'
          : linkIconGapStyles[size]
        : 'gap-2';
    const showLeadingIcon = Boolean(
      !isFormAction &&
        resolvedIconName &&
        (iconPosition === 'leading' || (iconOnly && !hasDefaultTypeIcon))
    );
    const showTrailingIcon = Boolean(
      !isFormAction && resolvedIconName && (hasDefaultTypeIcon || iconPosition === 'trailing')
    );

    const customVariantBorderRadiusClasses = isCustomVariant
      ? size === 'xs'
        ? 'rounded-tl-lg rounded-br-lg'
        : size === 's'
          ? 'rounded-tl-xl rounded-br-xl'
          : undefined
      : undefined;
    const contentTransformClass = isCustomVariant ? 'skew-x-12' : undefined;
    const componentClassName = cn(
      baseStyles,
      sizing,
      padding,
      gapClass,
      palette,
      isCustomVariant && '-skew-x-12',
      customVariantBorderRadiusClasses,
      isFormAction && 'relative overflow-hidden',
      className
    );
    const actionStateLabels: ReadonlyArray<{ key: CompButtonActionStateName; label: string }> = [
      { key: 'loading', label: ariaLabelLoadingState ?? 'Loading...' },
      { key: 'success', label: ariaLabelSuccessState ?? 'Success' },
      { key: 'error', label: ariaLabelErrorState ?? 'Error' },
      { key: 'feedback', label: ariaLabelFeedbackState ?? 'Feedback' },
    ];
    const actionStateAriaLabel = actionStateLabels.find(({ key }) =>
      Boolean(actionsStates?.[key])
    )?.label;
    const liveRegionLabel =
      actionStateAriaLabel && actionStateAriaLabel !== resolvedAriaLabel
        ? actionStateAriaLabel
        : undefined;
    const liveRegion = liveRegionLabel ? (
      <span
        aria-atomic="true"
        aria-live={actionsStates?.error ? 'assertive' : 'polite'}
        className="sr-only"
        data-testid="comp-button-live-region"
      >
        {liveRegionLabel}
      </span>
    ) : null;
    const commonDataProps = {
      'data-area': area,
      'data-component': 'CompButton',
      'data-type': type,
      'data-variant': variant,
    };
    const emitTrack = (event: React.MouseEvent<HTMLElement>) => {
      if (resolvedTrackHandlers.length === 0) {
        return;
      }

      const trackingPayload: CompButtonTrackPayload = {};

      if (customTrackingData) {
        trackingPayload.customTrackingData = customTrackingData;
      }

      if (iconOnly && iconName) {
        trackingPayload.trackingIconName = iconName;
      }

      for (const [index, trackHandler] of resolvedTrackHandlers.entries()) {
        if (index > 0 && trackHandler === resolvedTrackHandlers[index - 1]) {
          continue;
        }

        trackHandler(event, trackingPayload);
      }
    };
    const setDrawerOpen = (open: boolean) => {
      setDrawerIsVisible(open);
      onDrawerOpenChange?.(open);
    };

    const renderedContent = isFormAction ? (
      <CompButtonFormActionContent
        actionsStates={actionsStates}
        ariaLabelErrorState={ariaLabelErrorState}
        ariaLabelFeedbackState={ariaLabelFeedbackState}
        ariaLabelLoadingState={ariaLabelLoadingState}
        ariaLabelSuccessState={ariaLabelSuccessState}
        buttonActionsContext={buttonActionsContext}
        content={content}
        contentTransformClass={contentTransformClass}
        formActionSlots={formActionSlots}
      />
    ) : (
      <>
        {showLeadingIcon ? (
          <CompButtonIcon
            name={resolvedIconName as string}
            size={resolvedIconSize}
            className={contentTransformClass}
            testId="comp-button-icon-leading"
          />
        ) : null}
        {!iconOnly ? (
          contentTransformClass ? (
            <span className={contentTransformClass}>{content}</span>
          ) : (
            content
          )
        ) : null}
        {showTrailingIcon ? (
          <CompButtonIcon
            name={resolvedIconName as string}
            size={resolvedIconSize}
            className={contentTransformClass}
            testId="comp-button-icon-trailing"
          />
        ) : null}
      </>
    );

    const renderedDrawer = showDrawer
      ? React.createElement(
          DrawerComponent ?? CompButtonDefaultDrawer,
          {
            ...drawerComponentProps,
            id: resolvedDrawerId,
            drawerId: resolvedDrawerId,
            open: drawerIsVisible,
            onOpenChange: setDrawerOpen,
            ariaLabelDrawer,
            ariaLabelDrawerButton,
            layout: drawerLayout,
            scale: drawerScale,
          },
          drawerContent
        )
      : null;
    const sharedElementProps = {
      ...commonDataProps,
      className: componentClassName,
      'aria-disabled': isDisabled || undefined,
    };
    const resolveRel = (
      rel?: string,
      target?: AnchorHTMLAttributes<HTMLAnchorElement>['target']
    ) => {
      if (target !== '_blank') {
        return rel;
      }

      const relParts = new Set((rel ?? '').split(/\s+/).filter(Boolean));

      relParts.add('noopener');
      relParts.add('noreferrer');

      return Array.from(relParts).join(' ');
    };
    const resolveRenderedElement = (): {
      component: ElementType;
      props: Record<string, unknown>;
    } => {
      if (LinkComponent && !showDrawer) {
        const { onClick, rel, tabIndex, target, ...customLinkPassthrough } = elementProps as Omit<
          CompButtonAsCustomLinkProps,
          keyof SharedCompButtonProps | 'href'
        >;

        const handleCustomLinkClick = (event: React.MouseEvent<HTMLElement>) => {
          if (isDisabled) {
            event.preventDefault();
            return;
          }

          onClick?.(event as React.MouseEvent<HTMLAnchorElement>);
          emitTrack(event);
        };

        const customLinkProps: CompButtonCustomLinkProps & Record<string, unknown> = {
          ...linkComponentProps,
          ...customLinkPassthrough,
          ...sharedElementProps,
          ...(href ? { href } : {}),
          ...(to ? { to } : {}),
          children: renderedContent,
          rel: resolveRel(rel, target),
          tabIndex: isDisabled ? -1 : tabIndex,
          target,
          'aria-label': resolvedAriaLabel,
          onClick: handleCustomLinkClick,
        };

        return {
          component: LinkComponent,
          props: customLinkProps,
        };
      }

      if (
        typeof resolvedNavigationHref === 'string' &&
        resolvedNavigationHref.length > 0 &&
        !showDrawer
      ) {
        const { onClick, rel, tabIndex, target, ...anchorProps } = elementProps as Omit<
          CompButtonAsAnchorProps,
          keyof SharedCompButtonProps | 'href' | 'to'
        >;

        const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (isDisabled) {
            event.preventDefault();
            return;
          }

          onClick?.(event);
          emitTrack(event as React.MouseEvent<HTMLElement>);
        };

        return {
          component: 'a',
          props: {
            ref: ref as ForwardedRef<HTMLAnchorElement>,
            href: resolvedNavigationHref,
            target,
            rel: resolveRel(rel, target),
            tabIndex: isDisabled ? -1 : tabIndex,
            ...sharedElementProps,
            onClick: handleAnchorClick,
            ...anchorProps,
            'aria-label': resolvedAriaLabel,
            children: renderedContent,
          },
        };
      }

      const { onClick, ...buttonProps } = elementProps as Omit<
        CompButtonAsButtonProps,
        keyof SharedCompButtonProps | 'href'
      >;

      const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (showDrawer && !isDisabled) {
          setDrawerOpen(!drawerIsVisible);
        }

        onClick?.(event);
        emitTrack(event as React.MouseEvent<HTMLElement>);
      };

      return {
        component: 'button',
        props: {
          ref: ref as ForwardedRef<HTMLButtonElement>,
          type: resolvedButtonType,
          ...sharedElementProps,
          disabled: isDisabled,
          'aria-controls': showDrawer ? resolvedDrawerId : undefined,
          'aria-pressed': showDrawer ? drawerIsVisible : undefined,
          onClick: handleButtonClick,
          ...buttonProps,
          'aria-label': resolvedAriaLabel,
          children: renderedContent,
        },
      };
    };
    const { component: RootComponent, props: rootProps } = resolveRenderedElement();

    return (
      <>
        {React.createElement(RootComponent, rootProps)}
        {liveRegion}
        {renderedDrawer}
      </>
    );
  }
);

CompButton.displayName = 'CompButton';

export { CompButton, CompButtonProvider };
